// src/components/JobView/Tabs/Modals/TaskDetailModal.js
// Ventana de detalle de una ficha de tarea (se abre al hacer clic en una tarjeta
// del Kanban). Muestra toda la información y las acciones según el tipo de tarea.
import React, { useState, useEffect } from 'react';
import { X, User, Clock, Check, Trash2, Pencil, Sparkles, Wrench, Eye } from 'lucide-react';
import MultiUserSelect from './MultiUserSelect';
import { userLabel } from '../../../../utils/taskAssignment';

export const TYPE_META = {
    system: { label: 'Corrección', badge: 'bg-red-100 text-red-700', card: 'border-l-4 border-red-400' },
    system_reproduction: { label: 'Reproducción', badge: 'bg-orange-100 text-orange-700', card: 'border-l-4 border-orange-400' },
    manual: { label: 'Checklist', badge: 'bg-indigo-100 text-indigo-700', card: 'border-l-4 border-indigo-400' },
};

const STATUSES = [
    { key: 'pending', label: 'Pendiente' },
    { key: 'in_progress', label: 'En progreso' },
    { key: 'completed', label: 'Hecho' },
];

const normalize = (s) => (s === 'completed' ? 'completed' : s === 'in_progress' ? 'in_progress' : 'pending');

const TaskDetailModal = ({ task, onClose, allUsers = [], onAssignSingle, onAssignMulti, onCorrect, onViewSheet, onMove, onDelete, onSaveTitle, formatDate }) => {
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(task ? task.title : '');

    useEffect(() => { setTitle(task ? task.title : ''); setEditing(false); }, [task]);

    if (!task) return null;
    const meta = TYPE_META[task.type] || TYPE_META.manual;
    const isManual = task.type === 'manual';
    const status = normalize(task.status);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex justify-center items-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <header className="p-4 border-b flex items-start justify-between gap-2">
                    <div className="flex-grow">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.badge}`}>{meta.label}</span>
                        {editing ? (
                            <div className="flex items-center gap-1 mt-2">
                                <input value={title} onChange={(e) => setTitle(e.target.value)} className="flex-grow p-1 border rounded text-sm" autoFocus />
                                <button onClick={() => { onSaveTitle(task, title); setEditing(false); }} className="p-1 text-green-600" title="Guardar"><Check size={16} /></button>
                                <button onClick={() => { setTitle(task.title); setEditing(false); }} className="p-1 text-gray-400" title="Cancelar"><X size={16} /></button>
                            </div>
                        ) : (
                            <h2 className="text-lg font-bold mt-1 flex items-center gap-2">
                                {task.title}
                                {isManual && <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-indigo-600" title="Editar título"><Pencil size={14} /></button>}
                            </h2>
                        )}
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={22} /></button>
                </header>

                <div className="p-4 space-y-4">
                    {task.description && <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.description}</p>}

                    {task.aiSuggestedAction && (
                        <div className="text-sm p-3 bg-purple-50 border border-purple-200 rounded text-purple-800">
                            <p className="font-semibold flex items-center gap-1"><Sparkles size={14} /> Acción sugerida (IA)</p>
                            <p className="mt-1">{task.aiSuggestedAction}</p>
                        </div>
                    )}

                    {/* Estado (también se puede cambiar arrastrando la ficha) */}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Estado</p>
                        <div className="flex gap-1">
                            {STATUSES.map((s) => (
                                <button
                                    key={s.key}
                                    onClick={() => onMove(task.id, s.key)}
                                    className={`flex-1 py-1.5 text-xs font-semibold rounded-md border ${status === s.key ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Asignación según tipo */}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Asignado a</p>
                        {isManual ? (
                            <MultiUserSelect
                                users={allUsers}
                                selectedIds={task.assignedToUids || (task.assignedTo ? [task.assignedTo] : [])}
                                onChange={(ids) => onAssignMulti(task, ids)}
                                placeholder="Asignar a..."
                            />
                        ) : task.type === 'system' ? (
                            <select value={task.assignedToUid || task.assignedTo || ''} onChange={(e) => onAssignSingle(task, e.target.value)} className="text-sm border-gray-300 rounded-md">
                                <option value="">Sin asignar</option>
                                {allUsers.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
                            </select>
                        ) : (
                            <p className="text-sm text-gray-700">{task.assignedToName || 'Producción'}</p>
                        )}
                    </div>

                    <div className="text-xs text-gray-400 space-y-1">
                        <p className="flex items-center gap-1"><User size={11} /> Creado por: {task.createdByName || task.createdBy}</p>
                        <p className="flex items-center gap-1"><Clock size={11} /> {formatDate(task.createdAt)}</p>
                    </div>
                </div>

                <footer className="p-4 border-t bg-gray-50 flex flex-wrap gap-2 justify-end">
                    {task.type === 'system' && (
                        <button onClick={() => { onCorrect(task); onClose(); }} className="px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1"><Wrench size={14} /> Corregir</button>
                    )}
                    {task.type === 'system_reproduction' && task.sheetId && (
                        <button onClick={() => { onViewSheet(task); onClose(); }} className="px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-1"><Eye size={14} /> Ver Plancha</button>
                    )}
                    {isManual && (
                        <button onClick={() => { onDelete(task.id); onClose(); }} className="px-3 py-1.5 text-sm font-semibold text-red-600 bg-red-50 rounded-md hover:bg-red-100 flex items-center gap-1"><Trash2 size={14} /> Eliminar</button>
                    )}
                    {status !== 'completed' && (
                        <button onClick={() => { onMove(task.id, 'completed'); onClose(); }} className="px-3 py-1.5 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center gap-1"><Check size={14} /> Completar</button>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default TaskDetailModal;
